/**
 * Gstr3bView.tsx — GSTR-3B Monthly Summary Return
 * Complete GST Form 3B structure per GST Council specification:
 *   3.1  — Outward supplies (a taxable, b zero-rated, c nil/exempt, d inward RCM)
 *   3.2  — Inter-state supplies breakdown
 *   4    — Eligible ITC (A available, B reversed, C net)
 *   5    — Exempt / Nil / Non-GST outward supplies
 *   6.1  — Payment of tax
 */
import { useMemo } from 'react';
import { useStore } from '../store/useStore';
import { db } from '../db/db';
import { useLiveQuery } from 'dexie-react-hooks';

const fmt = (n: number) =>
  n.toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 });

export const Gstr3bView = ({ onQuit }: any) => {
  const activeCompany = useStore((s) => s.activeCompany);
  const fromDate      = useStore((s) => s.fromDate);
  const toDate        = useStore((s) => s.toDate);

  const data = useLiveQuery(async () => {
    if (!activeCompany) return null;

    const vouchers = await db.vouchers
      .where('companyId').equals(activeCompany.id!)
      .filter(v => v.date >= fromDate && v.date <= toDate && !v.deleted)
      .toArray();

    const vIds    = vouchers.map(v => v.id!);
    const entries = await db.voucherEntries.where('voucherId').anyOf(vIds).toArray();
    const ledgers = await db.ledgers.where('companyId').equals(activeCompany.id!).toArray();
    const ledgerMap = new Map(ledgers.map(l => [l.id!, l]));
    const groups  = await db.groups.where('companyId').equals(activeCompany.id!).toArray();
    const groupMap = new Map(groups.map(g => [g.id!, g.name]));

    const entriesByVoucher = new Map<number, typeof entries>();
    entries.forEach(e => {
      if (!entriesByVoucher.has(e.voucherId)) entriesByVoucher.set(e.voucherId, []);
      entriesByVoucher.get(e.voucherId)!.push(e);
    });

    // Accumulators
    let outTaxable = 0, outCgst = 0, outSgst = 0, outIgst = 0, outCess = 0;
    let outInterTaxable = 0, outInterCgst = 0, outInterSgst = 0, outInterIgst = 0;
    let nilExemptTaxable = 0;
    let inTaxable  = 0, inCgst = 0, inSgst = 0, inIgst = 0, inCess = 0;

    vouchers.forEach(v => {
      const vEntries = entriesByVoucher.get(v.id!) || [];
      const isOutward   = ['Sales', 'Credit Note'].includes(v.type);
      const isPurchase  = ['Purchase', 'Debit Note'].includes(v.type);

      let taxable = 0, cgst = 0, sgst = 0, igst = 0, cess = 0;
      vEntries.forEach(e => {
        const l = ledgerMap.get(e.ledgerId);
        if (!l) return;
        const amt = e.amount || 0;
        const grp = l.groupId ? groupMap.get(l.groupId) : '';
        if (l.name === 'CGST') cgst += amt;
        else if (l.name === 'SGST') sgst += amt;
        else if (l.name === 'IGST') igst += amt;
        else if (l.name === 'CESS') cess += amt;
        else if (isOutward && ['Sales Accounts', 'Direct Incomes', 'Indirect Incomes'].includes(grp || '')) taxable += amt;
        else if (isPurchase && ['Purchase Accounts', 'Direct Expenses'].includes(grp || '')) taxable += amt;
      });

      const totalTax = cgst + sgst + igst;
      const isNilExempt = taxable > 0 && totalTax === 0;
      const isInterState = igst > 0; // igst implies inter-state

      if (isOutward) {
        if (isNilExempt) {
          nilExemptTaxable += taxable;
        } else {
          outTaxable += taxable; outCgst += cgst; outSgst += sgst; outIgst += igst; outCess += cess;
          if (isInterState) {
            outInterTaxable += taxable; outInterIgst += igst;
          }
        }
      }
      if (isPurchase) {
        inTaxable += taxable; inCgst += cgst; inSgst += sgst; inIgst += igst; inCess += cess;
      }
    });

    const outputTax  = outCgst + outSgst + outIgst + outCess;
    const itcTotal   = inCgst + inSgst + inIgst + inCess;
    const netCgst    = Math.max(0, outCgst - inCgst);
    const netSgst    = Math.max(0, outSgst - inSgst);
    const netIgst    = Math.max(0, outIgst - inIgst);
    const netCess    = Math.max(0, outCess - inCess);
    const netPayable = netCgst + netSgst + netIgst + netCess;

    return {
      outward:     { taxable: outTaxable,      cgst: outCgst,   sgst: outSgst,   igst: outIgst,   cess: outCess,  total: outputTax },
      outInterState: { taxable: outInterTaxable, igst: outInterIgst },
      nilExempt:   { taxable: nilExemptTaxable },
      inward:      { taxable: inTaxable,       cgst: inCgst,    sgst: inSgst,    igst: inIgst,    cess: inCess  },
      itc:         { cgst: inCgst, sgst: inSgst, igst: inIgst, cess: inCess, total: itcTotal },
      net:         { cgst: netCgst, sgst: netSgst, igst: netIgst, cess: netCess, payable: netPayable },
    };
  }, [activeCompany, fromDate, toDate]);

  if (!activeCompany) return null;

  const fmtDate = (iso: string) => {
    if (!iso) return '';
    const [y, m, d] = iso.split('-');
    const months = ['Jan','Feb','Mar','Apr','May','Jun','Jul','Aug','Sep','Oct','Nov','Dec'];
    return `${d} ${months[parseInt(m) - 1]} ${y}`;
  };

  const SectionHeader = ({ num, label }: { num: string; label: string }) => (
    <div className="flex items-center px-3 py-1.5 bg-[#1e8585] text-white font-bold text-[11px] uppercase tracking-wide mt-2 first:mt-0">
      <span className="bg-white/20 px-2 py-[1px] rounded mr-2 text-[10px] font-mono">{num}</span>
      {label}
    </div>
  );

  const ColHeader = () => (
    <div className="flex items-center px-3 py-1.5 border-b-2 border-[#aebec8] bg-gray-50 font-bold text-[10px] uppercase tracking-wide text-gray-500 sticky top-0 z-10">
      <div className="flex-1">Particulars</div>
      <div className="w-24 text-right">CGST (₹)</div>
      <div className="w-24 text-right">SGST (₹)</div>
      <div className="w-24 text-right">IGST (₹)</div>
      <div className="w-24 text-right">CESS (₹)</div>
      <div className="w-28 text-right font-bold">Total (₹)</div>
    </div>
  );

  const Row = ({ label, cgst, sgst, igst, cess, value, bold = false, sub = false, indent = 0 }: any) => {
    const total = value !== undefined ? value
      : (cgst || 0) + (sgst || 0) + (igst || 0) + (cess || 0);
    const bg = bold ? 'bg-[#f0fafa]' : sub ? 'bg-[#fafbfc]' : 'bg-white';
    const textCls = sub ? 'text-gray-500 text-[10px]' : bold ? 'text-gray-800' : 'text-gray-700';
    return (
      <div className={`flex items-center border-b px-3 py-[5px] ${bg} ${bold ? 'border-[#cde8e8] font-bold' : 'border-gray-100'}`}>
        <div className={`flex-1 ${textCls}`} style={{ paddingLeft: indent * 16 }}>{label}</div>
        <div className="w-24 text-right tabular-nums font-mono text-[11px]">{cgst !== undefined ? fmt(cgst) : '—'}</div>
        <div className="w-24 text-right tabular-nums font-mono text-[11px]">{sgst !== undefined ? fmt(sgst) : '—'}</div>
        <div className="w-24 text-right tabular-nums font-mono text-[11px]">{igst !== undefined ? fmt(igst) : '—'}</div>
        <div className="w-24 text-right tabular-nums font-mono text-[11px]">{cess !== undefined ? fmt(cess) : '—'}</div>
        <div className={`w-28 text-right tabular-nums font-mono text-[11px] font-bold ${bold ? 'text-[#1e8585]' : 'text-gray-800'}`}>
          {total > 0 ? `₹${fmt(total)}` : '—'}
        </div>
      </div>
    );
  };

  const SingleRow = ({ label, value, bold = false, sub = false }: any) => (
    <div className={`flex items-center border-b px-3 py-[5px] ${bold ? 'bg-[#f0fafa] border-[#cde8e8] font-bold' : sub ? 'bg-[#fafbfc] border-gray-100' : 'bg-white border-gray-100'}`}>
      <div className={`flex-1 text-[11px] ${sub ? 'text-gray-500' : bold ? 'text-gray-800' : 'text-gray-700'}`}>{label}</div>
      <div className={`w-28 text-right tabular-nums font-mono text-[11px] font-bold ${bold ? 'text-[#1e8585]' : 'text-gray-800'}`}>
        {value !== undefined && value !== null ? `₹${fmt(value)}` : '—'}
      </div>
    </div>
  );

  return (
    <div className="flex flex-col w-full h-full bg-white z-20 absolute inset-0 font-sans text-[12px] text-black">
      <div className="bg-[#1e8585] text-white flex justify-between px-2 py-[2px] font-bold shadow-sm z-10">
        <span className="w-1/3">GSTR-3B — Monthly Summary Return</span>
        <span className="w-1/3 text-center">{activeCompany.name}</span>
        <span className="w-1/3 text-right cursor-pointer hover:text-gray-300" onClick={onQuit}>✕</span>
      </div>

      <div className="bg-white flex items-center justify-between px-3 py-1.5 font-bold border-b border-[#aebec8] shadow-sm shrink-0">
        <div className="flex items-center gap-3">
          <span className="text-[#1e8585] text-[11px]">Return Period</span>
          <span className="text-[11px] font-normal text-gray-500">{fmtDate(fromDate)} to {fmtDate(toDate)}</span>
        </div>
        {(activeCompany as any).gstin && (
          <span className="text-[10px] font-mono text-gray-400 bg-gray-50 px-2 py-[2px] rounded border border-gray-200">
            GSTIN: {(activeCompany as any).gstin}
          </span>
        )}
      </div>

      {data && (
        <div className="flex items-stretch gap-0 shrink-0 border-b border-[#aebec8]">
          {[
            { label: 'Output Tax',        val: data.outward.total,   color: 'text-gray-800' },
            { label: 'Input Tax Credit',  val: data.itc.total,       color: 'text-emerald-600' },
            { label: 'Nil / Exempt',      val: data.nilExempt.taxable, color: 'text-blue-600' },
            { label: 'Net GST Payable',   val: data.net.payable,     color: data.net.payable > 0 ? 'text-rose-700' : 'text-emerald-700' },
          ].map(c => (
            <div key={c.label} className="flex-1 px-3 py-2 border-r last:border-0 border-gray-200 bg-white">
              <div className="text-[9px] text-gray-500 font-semibold uppercase tracking-wide">{c.label}</div>
              <div className={`text-[15px] font-bold font-mono ${c.color}`}>₹{fmt(c.val)}</div>
            </div>
          ))}
        </div>
      )}

      <div className="flex-1 overflow-auto bg-white">
        {!data ? (
          <div className="flex flex-col items-center justify-center h-full text-gray-400">
            <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-[#1e8585] mb-2"></div>
            Computing GSTR-3B...
          </div>
        ) : (
          <div className="w-full">
            <ColHeader />

            {/* 3.1 — Outward Supplies */}
            <SectionHeader num="3.1" label="Details of Outward Supplies and Inward Supplies liable to Reverse Charge" />
            <Row label="(a) Outward taxable supplies (other than zero rated, nil, exempted)"
              cgst={data.outward.cgst} sgst={data.outward.sgst} igst={data.outward.igst} cess={data.outward.cess} />
            <div className="flex items-center border-b border-gray-100 px-3 py-[4px] bg-[#fafbfc]">
              <div className="flex-1 text-gray-500 text-[10px] pl-4">↳ Taxable Value</div>
              <div className="w-24 text-right text-[10px] text-gray-400 tabular-nums font-mono">—</div>
              <div className="w-24 text-right text-[10px] text-gray-400 tabular-nums font-mono">—</div>
              <div className="w-24 text-right text-[10px] text-gray-400 tabular-nums font-mono">—</div>
              <div className="w-24 text-right text-[10px] text-gray-400 tabular-nums font-mono">—</div>
              <div className="w-28 text-right text-[10px] text-gray-500 tabular-nums font-mono font-semibold">₹{fmt(data.outward.taxable)}</div>
            </div>
            <Row label="(b) Outward taxable supplies (zero rated)" cgst={undefined} sgst={undefined} igst={0} cess={0} value={0} sub />
            <Row label="(c) Other outward supplies (nil rated, exempt)" value={data.nilExempt.taxable} sub />
            <Row label="(d) Inward supplies (liable to reverse charge)" cgst={0} sgst={0} igst={0} cess={0} sub />
            <Row label="(e) Non-GST outward supplies" value={0} sub />
            <Row label="Total Output Tax Liability" cgst={data.outward.cgst} sgst={data.outward.sgst} igst={data.outward.igst} cess={data.outward.cess} bold />

            {/* 3.2 — Inter-state Supplies */}
            <SectionHeader num="3.2" label="Inter-state Supplies (supplies to unregistered, composition, UIN persons)" />
            <Row label="Supplies made to unregistered persons" igst={data.outInterState.igst} value={data.outInterState.taxable} />
            <Row label="Supplies made to composition taxable persons" igst={0} value={0} sub />
            <Row label="Supplies made to UIN holders" igst={0} value={0} sub />

            {/* 4 — ITC */}
            <SectionHeader num="4" label="Eligible ITC (Input Tax Credit)" />
            <Row label="(A) ITC Available — All inward supplies except exempt and blocked"
              cgst={data.itc.cgst} sgst={data.itc.sgst} igst={data.itc.igst} cess={data.itc.cess} />
            <div className="flex items-center border-b border-gray-100 px-3 py-[4px] bg-[#fafbfc]">
              <div className="flex-1 text-gray-500 text-[10px] pl-4">↳ Inward taxable value (purchases)</div>
              <div className="w-24"></div><div className="w-24"></div><div className="w-24"></div><div className="w-24"></div>
              <div className="w-28 text-right text-[10px] text-gray-500 tabular-nums font-mono font-semibold">₹{fmt(data.inward.taxable)}</div>
            </div>
            <Row label="(B) ITC Reversed (Rule 42 / 43 / Section 17(5) blocked credits)" cgst={0} sgst={0} igst={0} cess={0} sub />
            <Row label="(C) Net ITC Available  [A − B]" cgst={data.itc.cgst} sgst={data.itc.sgst} igst={data.itc.igst} cess={data.itc.cess} bold />

            {/* 5 — Exempt / Nil / Non-GST */}
            <SectionHeader num="5" label="Exempt, Nil and Non-GST Inward Supplies" />
            <SingleRow label="Inward supplies from composition taxpayers" value={0} />
            <SingleRow label="Inward supplies (exempt)" value={0} sub />
            <SingleRow label="Non-GST inward supplies" value={0} sub />

            {/* 6.1 — Payment */}
            <SectionHeader num="6.1" label="Payment of Tax" />
            <Row label="Tax on outward supplies (output tax)"
              cgst={data.outward.cgst} sgst={data.outward.sgst} igst={data.outward.igst} cess={data.outward.cess} />
            <Row label="Less: ITC claimed (set-off against output tax)"
              cgst={data.itc.cgst} sgst={data.itc.sgst} igst={data.itc.igst} cess={data.itc.cess} sub indent={1} />
            <Row
              label="Tax Payable (to be deposited via Cash Ledger)"
              cgst={data.net.cgst} sgst={data.net.sgst} igst={data.net.igst} cess={data.net.cess}
              bold
            />
            {data.net.payable > 0 && (
              <div className="px-3 py-2 bg-rose-50 border border-rose-200 mx-3 my-2 rounded text-[11px] text-rose-700 font-semibold">
                ⚠ Net tax payable: ₹{fmt(data.net.payable)} — Pay via GST Portal &gt; Payments &gt; Create Challan before due date.
              </div>
            )}
            {data.net.payable === 0 && (
              <div className="px-3 py-2 bg-emerald-50 border border-emerald-200 mx-3 my-2 rounded text-[11px] text-emerald-700 font-semibold">
                ✓ No net tax payable — ITC fully offsets output tax liability.
              </div>
            )}

            <div className="px-3 py-2 mt-1 text-[10px] text-gray-400 border-t border-gray-100 bg-gray-50">
              ⚠ Auto-computed from vouchers in the selected period. Nil/exempt outward supplies counted in 3.1(c).
              Blocked ITC (Section 17(5)), RCM liability, and inter-state composition supplies must be entered manually before portal filing.
              This is not a substitute for actual GSTR-3B filing on the GST portal.
            </div>
          </div>
        )}
      </div>
    </div>
  );
};
